import React, { useRef, useState, useEffect, useCallback } from "react";
import { useGetAgentStatusQuery } from "../../services/rtkApi/agentApi";

const statusColors: Record<string, string> = {
  in_chat: "#22c55e",
  online: "#22c55e",
  idle: "#eab308",
  busy: "#eab308",
  away: "#a855f7",
  offline: "#ef4444",
  out_of_tokens: "#ea580c",
};

const statusFallbacks: Record<string, string> = {
  in_chat: "in chat",
  online: "online",
  idle: "idle",
  busy: "idle",
  away: "away",
  offline: "offline",
  out_of_tokens: "out of credit",
};

/** tailwind-ish pill + label colors per derived status */
function statusPillClass(status: string): string {
  switch (status) {
    case "in_chat":
    case "online":
      return "bg-emerald-500/12 border border-emerald-500/25";
    case "idle":
    case "busy":
      return "bg-amber-500/12 border border-amber-500/20";
    case "away":
      return "bg-violet-500/12 border border-violet-500/25";
    case "out_of_tokens":
      return "bg-orange-600/20 border border-orange-600/40";
    case "offline":
    default:
      return "bg-red-500/10 border border-red-500/25";
  }
}

function statusLabelClass(status: string): string {
  if (status === "out_of_tokens") return "text-orange-900";
  if (status === "in_chat" || status === "online") return "text-emerald-900";
  if (status === "away") return "text-violet-900";
  if (status === "offline") return "text-red-900";
  return "text-zinc-600";
}

export function AgentStatusBadge({ className = "" }: { className?: string }) {
  const { data } = useGetAgentStatusQuery(undefined, {
    pollingInterval: 30_000,
  });

  const rawStatus = data?.status || "offline";
  const isInChat = rawStatus === "in_chat" ||
    (rawStatus === "busy" && !!data?.presence);
  const status = isInChat ? "in_chat" : rawStatus;
  const color = statusColors[status] || statusColors.offline;
  const label = data?.current_activity || statusFallbacks[status] || status;

  const containerRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflows, setOverflows] = useState(false);

  const GAP = 32; // px gap between the two copies

  const measure = useCallback(() => {
    const text = textRef.current;
    const container = containerRef.current;
    if (!text || !container) return;
    // measure just the first copy (half of the inner content minus half the gap)
    setOverflows(text.scrollWidth / 2 - GAP / 2 > container.clientWidth);
  }, []);

  useEffect(() => { measure(); }, [label, measure]);

  // scroll distance = width of one copy + gap, so it loops seamlessly
  const singleWidth = textRef.current ? (textRef.current.scrollWidth - GAP) / 2 : 0;
  const dist = singleWidth + GAP;
  const PX_PER_SEC = 18; // constant scroll speed
  const duration = dist > 0 ? dist / PX_PER_SEC : 14;

  return (
    <span
      className={`inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${statusPillClass(status)} ${className}`}
    >
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        {status === "in_chat" && (
          <span
            className="absolute inline-flex h-full w-full rounded-full opacity-50 animate-ping"
            style={{ backgroundColor: color }}
          />
        )}
        <span
          className="relative inline-flex h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: color }}
        />
      </span>
      <span
        ref={containerRef}
        className="min-w-0 flex-1 overflow-hidden"
        style={{
          maskImage: overflows
            ? "linear-gradient(to right, transparent, black 6px, black calc(100% - 6px), transparent)"
            : undefined,
          WebkitMaskImage: overflows
            ? "linear-gradient(to right, transparent, black 6px, black calc(100% - 6px), transparent)"
            : undefined,
        }}
      >
        <span
          ref={textRef}
          className={`inline-block whitespace-nowrap ${statusLabelClass(status)}`}
          style={
            overflows
              ? {
                  animation: `agent-status-scroll ${duration}s linear infinite`,
                  ["--scroll-dist" as string]: `-${dist}px`,
                }
              : undefined
          }
        >
          {label}
          {overflows && (
            <span style={{ paddingLeft: GAP }} aria-hidden="true">{label}</span>
          )}
        </span>
      </span>
    </span>
  );
}
